<script setup lang="ts">
import { useLoginSchema } from '@layers/common/app/schemas'
import { InvalidCredentialsError } from '@layers/common/app/services/errors'
import { useForm } from 'vee-validate'

const { refetchActiveOrder } = useActiveOrderQuery()
const { t } = useI18n()
const { ecommerce } = useAnalytics()

const { isSubmitting, handleSubmit, setFieldError } = useForm({
  validationSchema: useLoginSchema(),
})

const showPassword = ref(false)
const errorMessage = ref('')

const onSubmit = handleSubmit(async (values) => {
  try {
    await authLogin(values)
    await refetchActiveOrder()
    ecommerce.login('email')
  }
  catch (error) {
    if (error instanceof InvalidCredentialsError) {
      setFieldError('password', error.message)
    }
    else {
      errorMessage.value = error instanceof Error ? error.message : t('validation.unknown.message')
    }
    console.error(error)
  }
})
</script>

<template>
  <form @submit="onSubmit">
    <div class="grid gap-y-6">
      <UiFormField v-slot="{ componentField }" name="emailAddress" :validate-on-model-update="false">
        <UiFormItem v-auto-animate>
          <UiFormLabel>{{ t(('form.email_address.label')) }}</UiFormLabel>
          <UiFormControl>
            <UiInput type="email" autocomplete="username" data-testid="login-emailAddress" v-bind="componentField" />
          </UiFormControl>
          <UiFormMessage />
        </UiFormItem>
      </UiFormField>

      <UiFormField v-slot="{ componentField }" name="password" :validate-on-model-update="false">
        <UiFormItem v-auto-animate>
          <div class="flex items-center justify-between">
            <UiFormLabel>{{ t('form.password.label') }}</UiFormLabel>
          </div>
          <UiFormControl>
            <div class="relative">
              <UiInput
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                data-testid="login-password"
                v-bind="componentField"
                class="pr-10"
              />
              <UiButton
                type="button"
                variant="ghost"
                size="icon"
                class="absolute right-0 top-0 z-1 h-full px-2.5 hover:bg-transparent"
                data-testid="togglePasswordVisibility"
                :aria-label="showPassword ? t('form.hide_password.label') : t('form.show_password.label')"
                @click="showPassword = !showPassword"
              >
                <Icon :name="showPassword ? 'lucide:eye-off' : 'lucide:eye'" class="size-6 text-xl" />
              </UiButton>
            </div>
          </UiFormControl>
          <UiFormMessage />
        </UiFormItem>
      </UiFormField>

      <div class="flex items-center justify-between">
        <UiFormField v-slot="{ value, handleChange }" type="checkbox" name="rememberMe">
          <UiFormItem class="flex flex-row gap-x-2 space-y-0">
            <UiFormControl>
              <UiCheckbox :checked="value" data-testid="login-rememberMe" @update:checked="handleChange" />
            </UiFormControl>
            <div class="leading-none space-y-1">
              <UiFormLabel>{{ t('form.remember_me.label') }}</UiFormLabel>
              <UiFormMessage />
            </div>
          </UiFormItem>
        </UiFormField>

        <NuxtLinkLocale to="/auth/reset-password" class="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary">
          {{ t('form.forgot_password.label') }}
        </NuxtLinkLocale>
      </div>

      <UiButton
        id="login"
        type="submit"
        data-testid="login-submit"
        class="w-full rounded-full"
        variant="orange"
        :disabled="isSubmitting"
      >
        <Icon v-if="isSubmitting" name="lucide:loader-circle" class="size-4 animate-spin" />
        {{ t('form.login.label') }}
      </UiButton>

      <UiAlert v-if="errorMessage" variant="destructive" class="mb-4">
        <Icon name="heroicons:exclamation-circle" mode="svg" />
        <UiAlertTitle>{{ errorMessage }}</UiAlertTitle>
      </UiAlert>
    </div>

    <div class="mt-6 px-8 text-center text-sm text-muted-foreground">
      <NuxtLinkLocale data-testid="registerLink" to="/auth/register" class="underline underline-offset-4 hover:text-primary">
        {{ t('form.create_account.label') }}
      </NuxtLinkLocale>
    </div>
  </form>
</template>
